//
import { getDefaultState } from './state';
import * as getters from './getters';
import { defineStore } from 'pinia';
import { Session } from '@/shared/util/session';
import type {
  ICustomerStore,
  ICustomerStoreCustomerProfile,
  ICustomerStoreInvestments,
  ICustomerStoreInvestmentsCalculator,
  ICustomerStoreInvestmentsGroup,
  ICustomerStoreInvestmentsGroupAccount,
  ICustomerStoreInvestmentsStatement,
  ICustomerStoreLoading,
} from './customerStore.model';
import { useGlobalStore } from '../global/global.store';
import { FEATURE_FLAGS } from '@/shared/models/constants.models';
import bff from '@/shared/api/bff';
import content from '@/shared/api/content';
import type { IDocumentMetadata } from '@/shared/api/models.content';
import type {
  IContactRepPayload,
  ICustodialAccountMeta,
  IInvestmentCalculatorScenario,
  ISearchCustodialAccountPayload,
} from '@/shared/api/models.bff';
import type { IGlobalError } from '@/stores/global/globalStore.model.ts';

export const useCustomerStore = defineStore('customer', {
  state: getDefaultState,
  getters,
  actions: {
    setBinaryInvestmentStatement(binaryInvestmentStatement: string | null) {
      this.binaryInvestmentStatement = binaryInvestmentStatement;
    },
    setError(error: string | null) {
      this.error = error;
      this.setLoading(false);
    },
    setLoading(componentLoading: ICustomerStoreLoading | boolean) {
      if (!componentLoading) {
        Object.keys(this.loading).forEach((key) => {
          this.loading[key] = false;
        });
      } else {
        const component = Object.keys(componentLoading)[0];
        this.loading[component] = (componentLoading as ICustomerStoreLoading)[component];
      }
    },
    setInvestments(investments: ICustomerStoreInvestments | null) {
      if (investments?.groups) {
        this.investments.groups = investments.groups;
      }
      if (investments?.calculator) {
        this.investments.calculator = investments.calculator;
      }
      if (investments?.summary) {
        this.investments.summary = investments.summary;
      }
      if (investments?.custodians) {
        this.investments.custodians = investments.custodians;
      }
      if (investments?.isCompliant) {
        this.investments.isCompliant = investments.isCompliant;
      }
    },
    setInvestmentCalculator(calculator: ICustomerStoreInvestmentsCalculator) {
      this.investments.calculator = calculator;
    },
    setInvestmentStatements(statements: ICustomerStoreInvestmentsStatement[]) {
      this.investments.statements = statements;
    },
    setSamlToken(token: string) {
      this.investments.samlToken = token;
    },
    updateCustomerProfile(profile: ICustomerStoreCustomerProfile) {
      this.customerProfile = Object.assign(this.customerProfile, profile);
      // analytics
      if (this.customerProfile.customerId) {
        Session.set('Q_ci', this.customerProfile.customerId.toString());
        if (typeof window.BrowserAgentExtension?.setCustomerId === 'function') {
          window.BrowserAgentExtension.setCustomerId({
            customerId: this.customerProfile.customerId,
          });
        }
      }
    },
    resetState(_state: ICustomerStore) {
      Object.assign(this, getDefaultState());
    },
    async getInvestments() {
      const globalStore = useGlobalStore();
      try {
        // Check the feature flag
        const investmentsFlag = globalStore.containsFeature(FEATURE_FLAGS.INVESTMENTS);
        const calculatorFlag = globalStore.containsFeature(FEATURE_FLAGS.RETIREMENT_CALCULATOR);
        if (investmentsFlag || calculatorFlag) {
          let features = '';
          if (investmentsFlag && !calculatorFlag) {
            features = FEATURE_FLAGS.INVESTMENTS;
          } else if (!investmentsFlag && calculatorFlag) {
            features = FEATURE_FLAGS.RETIREMENT_CALCULATOR;
          }
          this.setLoading({ investments: true } as ICustomerStoreLoading);
          const investments: ICustomerStoreInvestments | null = await bff.getInvestments(
            this.customerProfile.country,
            features
          );
          this.setInvestments(investments);

          // Get agents for investments
          if (investments.groups && investments.groups.length > 0) {
            const ids: string[] = [];
            investments.groups.forEach((group: ICustomerStoreInvestmentsGroup) => {
              group.accounts?.forEach((a: ICustomerStoreInvestmentsGroupAccount) => {
                if (a.agentId && !globalStore.getAgent(a.agentId) && !ids.includes(a.agentId)) {
                  ids.push(a.agentId);
                }
              });
            });
            if (ids.length > 0) {
              const agents = await bff.getAgents(ids);
              globalStore.addAgents(agents);
            }
          }
        }
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      } finally {
        this.setLoading({ investments: false } as ICustomerStoreLoading);
      }
    },
    async getInvestmentStatements() {
      const globalStore = useGlobalStore();
      try {
        this.setLoading({ investments: true } as ICustomerStoreLoading);
        const response = await bff.getInvestmentStatementMetadata();
        this.setInvestmentStatements(response.statements);
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      } finally {
        this.setLoading({ investments: false } as ICustomerStoreLoading);
      }
    },
    async openStatement(statementMetadata: IDocumentMetadata) {
      const globalStore = useGlobalStore();
      try {
        this.setLoading({ investmentStatements: true } as ICustomerStoreLoading);
        const binaryStatement = await content.streamDocument(statementMetadata);
        this.setBinaryInvestmentStatement(binaryStatement);
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      } finally {
        this.setLoading({ investmentStatements: false } as ICustomerStoreLoading);
      }
    },
    async getInvestmentCalculator(scenario: IInvestmentCalculatorScenario) {
      const globalStore = useGlobalStore();
      try {
        this.setLoading({ retirementCalculator: true } as ICustomerStoreLoading);
        const res = await bff.getInvestmentCalculator(scenario);
        this.setInvestmentCalculator(res);
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      } finally {
        this.setLoading({ retirementCalculator: false } as ICustomerStoreLoading);
      }
    },
    async getSamlToken(scenario: IInvestmentCalculatorScenario) {
      const globalStore = useGlobalStore();
      try {
        // this.setLoading({ retirementCalculator: true });
        const res = await bff.getSamlToken(scenario);
        this.setSamlToken(res?.samltoken);
      } catch (e) {
        globalStore.addError({ error: e, context: 'app' } as IGlobalError);
      } finally {
        // this.setLoading({ retirementCalculator: false });
      }
    },
    async contactRepresentative(emailPayload: IContactRepPayload) {
      this.sendRepEmailError = false;
      try {
        await bff.sendRepEmail(emailPayload);
      } catch (_e) {
        this.sendRepEmailError = true;
      }
    },
    async searchMinorAccount(
      payload: ISearchCustodialAccountPayload
    ): Promise<ICustodialAccountMeta[]> {
      return bff.searchMinorAccount(payload);
    },
    async linkMinorAccount(accountId: string): Promise<boolean> {
      return bff.linkMinorAccount(accountId);
    },
    async getAccountNumber(accountId: string) {
      return bff.getAccountNumber(accountId);
    },
  },
});
